import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import VerifyCode from 'src/pages/authentication/VerifyCode';
import ResetPassword from 'src/pages/authentication/ResetPassword';
import LoginFormV2, {
  emailRegex,
  LOGIN_FORM_STEP,
  phoneRegex,
} from './LoginFormV2';
import LoginLinkSent from './LoginLinkSent';
import TermsOfServiceDialogV2 from './TermsOfServiceDialogV2';
import axios from 'src/utils/axios';
import useAuth from 'src/hooks/useAuth';
import { useNavigate } from 'react-router';
import { INVITE_CONTACTS_VERIFIED_STORAGE_KEY } from 'src/hooks/useInviteContactsVerified';
import {
  clearPendingPhoneLogin,
  readPendingPhoneLogin,
  writePendingPhoneLogin,
} from './pendingPhoneLogin';

const validationSchema = Yup.object().shape({
  username: Yup.string()
    .required('This field is required')
    .test(
      'is-email-or-phone',
      'Please enter a valid phone number or email address.',
      (value) => {
        if (!value) {
          return false;
        }
        return emailRegex.test(value) || phoneRegex.test(value);
      }
    ),
  password: Yup.string().required('Password is required.'),
});

interface LoginV2Props {
  extendSession?: boolean;
  onExtendSessionSuccess?: ({ refresh }) => void;
  returnUrl?: string | null;
  onLoginSuccess?: () => void;
  onStepChange?: (step: LOGIN_FORM_STEP) => void;
  /** Skip TOS check (e.g. self-registration modal - existing residents applying) */
  skipTermsOfService?: boolean;
  /** Compact/embedded mode: hide logo and "Login" heading (e.g. inline in self-registration) */
  compact?: boolean;
}

interface SessionDetails {
  user: number;
  access: string;
  refresh: string;
}

const LoginStateManager: FC<LoginV2Props> = ({
  extendSession = false,
  onExtendSessionSuccess,
  returnUrl = null,
  onLoginSuccess,
  onStepChange,
  skipTermsOfService = false,
  compact = false,
}) => {
  const { login, isAuthenticated, extendCurrentSession } = useAuth();
  const termsOfServiceRef = useRef(null);
  const navigate = useNavigate();

  const [loginError, setLoginError] = useState<null | string>(null);
  const [sessionDetails, setSessionDetails] = useState<SessionDetails>(null);
  const [phoneCooldownSeconds, setPhoneCooldownSeconds] = useState(0);

  // ENG-4123: only the standalone /login page persists a pending phone login.
  // The extend-session modal and the self-registration embed keep their
  // in-memory-only behaviour.
  const isStandaloneLogin = !extendSession && !compact;

  // Read once, before the first render picks a step: a full page load on the
  // code-entry step has to land back on the code form, with the phone it was
  // sent to, so the already-issued code can still be entered.
  const [restoredPendingLogin] = useState(() =>
    isStandaloneLogin ? readPendingPhoneLogin() : null
  );

  const [loginFormStep, setLoginFormStep] = useState<LOGIN_FORM_STEP>(
    restoredPendingLogin ? LOGIN_FORM_STEP.VERIFY_CODE : LOGIN_FORM_STEP.LOGIN
  );

  useEffect(() => {
    onStepChange?.(loginFormStep);
  }, [loginFormStep, onStepChange]);

  const formik = useFormik({
    initialValues: {
      username: restoredPendingLogin?.phone ?? '',
      password: '',
    },
    validationSchema,
    onSubmit: () => {
      setLoginError(null);
      void onLogin();
    },
  });

  const { values, resetForm, setSubmitting } = formik;
  const { username, password } = values;

  // ENG-4123: mirror the step into storage so a reload can restore it. Written
  // only while the six-digit form is on screen with a phone to send to; every
  // other step (including Back) clears it, so a stale phone is never restored.
  useEffect(() => {
    if (!isStandaloneLogin) {
      return;
    }
    if (loginFormStep === LOGIN_FORM_STEP.VERIFY_CODE && username) {
      writePendingPhoneLogin(username);
    } else {
      clearPendingPhoneLogin();
    }
  }, [isStandaloneLogin, loginFormStep, username]);

  // TODO: Remove this further down the line
  //  this is just to cleanup exposed data
  useEffect(() => {
    localStorage.removeItem('temporalSession');
    localStorage.removeItem('email');
  }, []);

  const onBack = useCallback((): void => {
    setLoginFormStep(LOGIN_FORM_STEP.LOGIN);
    setPhoneCooldownSeconds(0);
  }, []);

  const navigateAfterLogin = useCallback((): void => {
    if (onLoginSuccess) {
      onLoginSuccess();
    } else {
      const target = returnUrl && returnUrl.startsWith('/') ? returnUrl : '/';
      navigate(target);
    }
  }, [onLoginSuccess, returnUrl, navigate]);

  const onTermsOfServiceAccept = async (): Promise<void> => {
    await axios.patch(`user_profile/user/${sessionDetails?.user}/`, {
      terms_of_service_agreement: true,
    });

    await login(sessionDetails.access, sessionDetails.refresh);
    navigateAfterLogin();
  };

  const onLogin = async (): Promise<void> => {
    try {
      const response = await axios.post('token/both/?validate', {
        email: username,
        password: password,
      });
      const { access, refresh } = response.data;

      // ENG-2317 ULCM Feedback: when the backend resolved one or more
      // pending INVITE_ACCEPT OTPs on this login (i.e. this is the
      // renter's first successful login after being invited), stash a
      // flag in sessionStorage so the post-login landing page can render
      // a one-time "Your contact has been verified" banner. The flag
      // clears itself when the banner reads it.
      //
      // No client-side flag check here — the backend serializer only
      // emits `invite_contacts_verified` when the renter has pending
      // invite OTPs, so its presence in the response IS the gate. The
      // banner render site (TenantHomePage) reads the flag from
      // sessionStorage, so no other client-side gate is needed.
      const resolvedInviteOtpIds = (
        response.data as { invite_contacts_verified?: string[] }
      )?.invite_contacts_verified;
      if (
        Array.isArray(resolvedInviteOtpIds) &&
        resolvedInviteOtpIds.length > 0
      ) {
        try {
          window.sessionStorage.setItem(
            INVITE_CONTACTS_VERIFIED_STORAGE_KEY,
            JSON.stringify(resolvedInviteOtpIds)
          );
        } catch {
          // SessionStorage is best-effort. If it fails (e.g. Safari
          // private browsing with quota=0) the banner just won't show.
        }
      }

      if (isAuthenticated) {
        extendCurrentSession(response.data);
        onExtendSessionSuccess?.(response.data);
        setSubmitting(false);
        setLoginError(null);
        resetForm();
        return;
      }

      if (!skipTermsOfService) {
        const { data: user } = await axios.get(
          'user_profile/user/current_profile/'
        );

        if (!user.terms_of_service_agreement) {
          setSessionDetails({
            user: user.id,
            access: access,
            refresh: refresh,
          });
          termsOfServiceRef.current?.toggleOpenDialog();
          return;
        }
      }

      await login(access, refresh);
      navigateAfterLogin();

      setSubmitting(false);
      resetForm();
    } catch (error) {
      const axErr = error as { status?: number; detail?: string };
      let errorText = 'Invalid credentials. Please verify them and try again.';
      if (axErr?.status === 429 && axErr?.detail) {
        errorText = axErr.detail;
      }
      setLoginError(errorText);
      setSubmitting(false);
    }
  };

  const getMainContent = useCallback((): React.ReactNode => {
    switch (loginFormStep) {
      case LOGIN_FORM_STEP.LOGIN:
        return (
          <LoginFormV2
            formik={formik}
            formStep={loginFormStep}
            setLoginFormStep={setLoginFormStep}
            extendSession={extendSession}
            loginError={loginError}
            compact={compact}
            onPhoneLoginCooldown={setPhoneCooldownSeconds}
          />
        );
      case LOGIN_FORM_STEP.VERIFY_CODE:
        return (
          <VerifyCode
            phone={username}
            onBack={onBack}
            onExtendSessionSuccess={onExtendSessionSuccess}
            onLoginSuccess={onLoginSuccess}
            returnUrl={returnUrl}
            skipTermsOfService={skipTermsOfService}
            initialCooldownSeconds={phoneCooldownSeconds}
          />
        );
      case LOGIN_FORM_STEP.LOGIN_LINK_SENT:
        return <LoginLinkSent onBack={onBack} />;
      case LOGIN_FORM_STEP.RESET_PASSWORD:
        return (
          <ResetPassword
            userEmail={username}
            onBack={() => setLoginFormStep(LOGIN_FORM_STEP.LOGIN)}
            extendSession={extendSession}
            showLogo={!extendSession && !compact}
            showCopyright={false}
            embedded={compact}
          />
        );
    }
  }, [
    compact,
    extendSession,
    formik,
    loginError,
    loginFormStep,
    onBack,
    onExtendSessionSuccess,
    onLoginSuccess,
    phoneCooldownSeconds,
    returnUrl,
    skipTermsOfService,
    username,
  ]);

  return (
    <>
      {getMainContent()}
      <TermsOfServiceDialogV2
        onAccept={onTermsOfServiceAccept}
        ref={termsOfServiceRef}
      />
    </>
  );
};

export default LoginStateManager;
